import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, LucideIcon } from 'lucide-react';

/**
 * Dropdown Menu Item interface
 */
export interface DropdownItem {
  key: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'info' | 'warning';
  disabled?: boolean;
}

/**
 * Dropdown Menu Props
 */
interface DropdownProps {
  items: DropdownItem[];
  trigger?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  usePortal?: boolean;
}

const variantStyles = {
  default: 'text-gray-700 hover:bg-gray-50',
  danger: 'text-red-700 hover:bg-red-50',
  success: 'text-green-700 hover:bg-green-50',
  info: 'text-blue-700 hover:bg-blue-50',
  warning: 'text-orange-700 hover:bg-orange-50',
};

/**
 * Pure, reusable Dropdown Menu component
 * Optimized for table actions with minimal space footprint
 */
export const Dropdown: FC<DropdownProps> = ({
  items,
  trigger,
  align = 'right',
  className = '',
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Update position when using portal
  useEffect(() => {
    if (isOpen && usePortal && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        left: align === 'right' ? rect.right + window.scrollX - 192 : rect.left + window.scrollX,
      });
    }
  }, [isOpen, usePortal, align]);

  const handleItemClick = (item: DropdownItem) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const alignmentClass = align === 'right' ? 'right-0' : 'left-0';

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const renderDropdownMenu = () => {
    if (!isOpen) return null;

    const menuContent = (
      <div
        ref={dropdownRef}
        className={`
          ${usePortal ? 'fixed' : 'absolute'} ${alignmentClass} mt-1 z-[9999]
          w-48 bg-white rounded-lg shadow-lg border border-gray-200
          animate-fadeIn
        `}
        style={usePortal ? { top: `${position.top}px`, left: `${position.left}px` } : undefined}
      >
        <div className="py-1">
          {items.map((item) => {
            const Icon = item.icon;
            const variant = item.variant || 'default';

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`
                  w-full px-4 py-2.5 text-left text-sm font-medium
                  flex items-center gap-3
                  transition-colors duration-150
                  ${variantStyles[variant]}
                  ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  first:rounded-t-lg last:rounded-b-lg
                `}
              >
                {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );

    // If using portal, render to document.body
    if (usePortal) {
      return createPortal(menuContent, document.body);
    }

    return menuContent;
  };

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label="Actions menu"
      >
        {trigger || <MoreVertical className="w-4 h-4" />}
      </button>

      {/* Dropdown Menu */}
      {renderDropdownMenu()}
    </div>
  );
};

